/**
 * The error ledger — Guardrail 10's persistent half.
 *
 * Guardrail 10: every dropped, failed or timed-out background operation produces
 * BOTH a user-visible signal and a persistent ledger entry. Sentry covers the
 * builder's view; this covers the field's. Five people on installed PWAs have no
 * console and no devtools, and the only support channel is one person. When a
 * stylist says "it didn't save", the ledger — copyable from the Diagnostics screen —
 * is the difference between a diagnosis and a guess.
 *
 * LESSON PINNED AT THE SCENE (Journal post-mortem §0.1, §2): every multi-week
 * incident in that project traced back to a failure that produced no signal
 * anywhere. Not a wrong signal. No signal.
 *
 * Design constraints this file honours:
 *  - Never throws. A ledger that can crash the app it instruments is worse than none.
 *  - Bounded. A ring buffer, so a failing retry loop cannot fill the device.
 *  - Survives storage death. If persistence fails, it keeps recording in memory AND
 *    records the persistence failure itself, so the Diagnostics screen can say
 *    "storage is full" rather than showing a suspiciously empty list.
 */
import { classifyError } from './classify.js';
import { MemoryStorage, safeGet, safeSet } from '../platform/storage.js';
import { instantISO } from '../time/date.js';
const DEFAULT_KEY = 'mononoke.error-ledger.v1';
const DEFAULT_MAX = 200;
const MAX_DETAIL_CHARS = 600;
/** Values that must never reach persistent storage or a support paste. */
const REDACT_PATTERNS = [
    /([?&])(X-Amz-Signature|X-Amz-Credential|Signature|AWSAccessKeyId)=[^&\s]+/gi,
    /(Bearer\s+)[A-Za-z0-9._~+/-]+=*/gi,
    /("?(password|token|api[_-]?key|secret|authorization)"?\s*[:=]\s*")[^"]*(")/gi,
];
export function redact(text) {
    let out = text;
    out = out.replace(REDACT_PATTERNS[0], '$1$2=REDACTED');
    out = out.replace(REDACT_PATTERNS[1], '$1REDACTED');
    out = out.replace(REDACT_PATTERNS[2], '$1REDACTED$3');
    return out;
}
function detailOf(error) {
    if (error instanceof Error) {
        const stack = error.stack ?? `${error.name}: ${error.message}`;
        return redact(stack).slice(0, MAX_DETAIL_CHARS);
    }
    if (typeof error === 'string')
        return redact(error).slice(0, MAX_DETAIL_CHARS);
    try {
        return redact(JSON.stringify(error)).slice(0, MAX_DETAIL_CHARS);
    }
    catch {
        return undefined;
    }
}
export class ErrorLedger {
    storage;
    key;
    maxEntries;
    appVersion;
    now;
    onEntry;
    remoteStatus;
    /** Mirrors persisted state; also the whole truth when persistence is dead. */
    entries = [];
    persistenceHealthy = true;
    constructor(options = {}) {
        this.storage = options.storage ?? new MemoryStorage();
        this.key = options.key ?? DEFAULT_KEY;
        this.maxEntries = options.maxEntries ?? DEFAULT_MAX;
        this.appVersion = options.appVersion ?? 'unknown';
        this.now = options.now ?? (() => new Date());
        this.onEntry = options.onEntry;
        this.remoteStatus = options.remoteStatus;
        this.entries = this.load();
    }
    load() {
        const raw = safeGet(this.storage, this.key);
        if (raw === null)
            return [];
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        }
        catch {
            // Corrupt ledger: start clean rather than crash, but say so in the ledger itself.
            return [
                {
                    at: instantISO(this.now()),
                    code: 'LEDGER_CORRUPT',
                    retryable: false,
                    transport: 'runtime',
                    matchedBy: 'default',
                    requiresReauth: false,
                    source: 'ledger',
                    userMessage: 'Diagnostics history was unreadable and has been reset.',
                    appVersion: this.appVersion,
                    level: 'error',
                    notified: false,
                },
            ];
        }
    }
    persist() {
        const outcome = safeSet(this.storage, this.key, JSON.stringify(this.entries));
        if (outcome.ok) {
            this.persistenceHealthy = true;
            return;
        }
        if (outcome.reason === 'quota' && this.entries.length > 10) {
            // Halve and retry once: losing old entries beats losing new ones.
            this.entries = this.entries.slice(-Math.floor(this.entries.length / 2));
            const retry = safeSet(this.storage, this.key, JSON.stringify(this.entries));
            if (retry.ok) {
                this.persistenceHealthy = true;
                return;
            }
        }
        // Storage is dead. Keep going in memory; the Diagnostics screen surfaces this
        // so nobody reads an empty ledger as "nothing went wrong".
        this.persistenceHealthy = false;
    }
    /** Never throws. Returns the entry so callers can show its userMessage. */
    record(error, source, options = {}) {
        let classified;
        try {
            classified = classifyError(error, options.classify);
        }
        catch {
            classified = {
                code: 'CLASSIFIER_FAILED',
                retryable: false,
                transport: 'unknown',
                matchedBy: 'default',
                requiresReauth: false,
                userMessage: 'Something went wrong and was not saved. Support has been notified.',
                original: error,
            };
        }
        const entry = {
            at: instantISO(this.now()),
            code: classified.code,
            retryable: classified.retryable,
            transport: classified.transport,
            matchedBy: classified.matchedBy,
            requiresReauth: classified.requiresReauth,
            source,
            userMessage: classified.userMessage,
            appVersion: this.appVersion,
            notified: options.notified ?? false,
            level: 'error',
            ...(options.context ? { context: options.context } : {}),
            ...(detailOf(error) !== undefined ? { detail: detailOf(error) } : {}),
        };
        this.entries.push(entry);
        if (this.entries.length > this.maxEntries) {
            this.entries = this.entries.slice(-this.maxEntries);
        }
        this.persist();
        try {
            this.onEntry?.(entry);
        }
        catch {
            // A failing Sentry transport must not take down the local ledger.
            this.persistenceHealthy = this.persistenceHealthy && true;
        }
        return entry;
    }
    /**
     * Record a deliberate, non-error outcome. Same ledger, same persistence, same
     * support text — but it never becomes a Sentry issue and never reads as a defect.
     * Never throws.
     */
    note(code, source, options) {
        const entry = {
            at: instantISO(this.now()),
            code,
            retryable: false,
            transport: 'runtime',
            matchedBy: 'explicit',
            requiresReauth: false,
            source,
            userMessage: options.userMessage,
            appVersion: this.appVersion,
            notified: options.notified ?? false,
            level: 'info',
            ...(options.context ? { context: options.context } : {}),
            ...(options.detail ? { detail: redact(options.detail).slice(0, MAX_DETAIL_CHARS) } : {}),
        };
        this.entries.push(entry);
        if (this.entries.length > this.maxEntries)
            this.entries = this.entries.slice(-this.maxEntries);
        this.persist();
        try {
            this.onEntry?.(entry);
        }
        catch {
            // A failing transport must not take down the local ledger.
        }
        return entry;
    }
    list() {
        return this.entries;
    }
    /** False means entries exist only in memory and die with the tab. Show this. */
    isPersistent() {
        return this.persistenceHealthy;
    }
    clear() {
        this.entries = [];
        this.persist();
    }
    /** The support payload: what a stylist copies out of Diagnostics and sends. */
    toSupportText() {
        const header = [
            `Mononoke diagnostics — ${instantISO(this.now())}`,
            `app version: ${this.appVersion}`,
            `entries: ${this.entries.length}${this.persistenceHealthy ? '' : ' (NOT PERSISTED — storage unavailable)'}`,
            `remote reporting: ${this.remoteStatus ? this.remoteStatus() : 'not wired'}`,
            '',
        ].join('\n');
        const body = this.entries
            .slice(-50)
            .map((e) => {
            const ctx = e.context ? ` ${JSON.stringify(e.context)}` : '';
            const seen = e.notified ? '' : ' [user NOT notified]';
            // An UNKNOWN entry that does not say what it was is a dead end. Seen in
            // production on 26 Jul 2026: four ledger lines reading
            // `UNKNOWN (sw-update, default, retryable=false)` with no way to tell what
            // had actually been thrown, so no classifier rule could be written for it.
            // `detail` was being captured all along and simply never rendered here --
            // the one artifact a human actually reads. When no rule matched, the report
            // now carries the first line of the throwable so the NEXT occurrence is
            // diagnosable instead of merely counted.
            const unidentified = e.matchedBy === 'default' && e.detail
                ? ` unclassified=${JSON.stringify(e.detail.split('\n', 1)[0]?.slice(0, 160) ?? '')}`
                : '';
            const mark = e.level === 'info' ? 'note ' : '';
            const reauth = e.requiresReauth ? ' REQUIRES-REAUTH' : '';
            return `${e.at} ${mark}${e.code} (${e.source}, ${e.matchedBy}, retryable=${String(e.retryable)})${reauth}${seen}${ctx}${unidentified}`;
        })
            .join('\n');
        return redact(`${header}${body}`);
    }
}
//# sourceMappingURL=ledger.js.map